import {message} from 'antd';
import moment from 'moment';
import React from 'react';

import {DateRangeInput} from 'components/@common/inputs/date-range-input';
import {EDateRangeInputSide} from 'components/@common/inputs/date-range-input/date-range-input-constants';
import {DATE_DMY, DATE_FOR_REQUEST} from 'shared/constants/date-format';
import {IReportConfigurationQueryParameterDto} from 'store/slices/report-configuration-slice/report-configuration-dto';

interface ITableReportDateRangeInputProps {
  paramName: string;
  label: string;
  format?: string;
  value?: any;
  onChange?: (value: any) => void;
  isClearable?: boolean;
  queryParameter?: IReportConfigurationQueryParameterDto;
  isDisabled?: boolean;
}

export const TableReportDateRangeInput = ({
    paramName,
    isDisabled,
    ...props
}: ITableReportDateRangeInputProps) => {
    const {periodLength, inputRestrictions} = props.queryParameter ?? {};
    const shouldShowInputRestrictions = inputRestrictions && periodLength;

    const getValue = () => {
        if (!props.value) return undefined;

        try {
            return JSON.parse(props.value);
        } catch (error) {
            return undefined;
        }
    };

    const disabledDate = (
        current: moment.Moment,
        dateValues: [string | undefined, string | undefined],
        types: string,
        panelMode: [string, string],
    ) => {
        if (panelMode?.includes('year')) {
            return false;
        }
        const dateIndex = types === EDateRangeInputSide.START ? 0 : 1;
        const referenceDate = dateValues?.[dateIndex];

        if (!referenceDate || !shouldShowInputRestrictions) return false;

        const formattedCurrent = moment(current, DATE_DMY);
        const formattedReference = moment(referenceDate, DATE_DMY);

        if (types === EDateRangeInputSide.START) {
            const maxDate = formattedReference.clone().add(periodLength, 'day');
            return formattedCurrent.isAfter(maxDate);
        }

        if (types === EDateRangeInputSide.END) {
            const minDate = formattedReference.clone().subtract(periodLength - 1, 'day');
            return formattedCurrent.isBefore(minDate);
        }
        return false;
    };

    const handleChange = (
        value: [string, string] | undefined, lastSelectedSide?: EDateRangeInputSide | undefined,
    ) => {
        if (shouldShowInputRestrictions) {
            const dateFrom = moment(value?.[0], DATE_FOR_REQUEST);
            const dateTo = moment(value?.[1], DATE_FOR_REQUEST);
            const diffDays = Math.abs(dateFrom.diff(dateTo, 'days'));
            const isOutOfRange = diffDays > periodLength;

            if (isOutOfRange) {
                const isFirstDateChanged = lastSelectedSide === EDateRangeInputSide.START;
                const dateToSet = isFirstDateChanged ? value?.[0] : value?.[1];

                message.error(`Диапазон дат не может быть больше ${periodLength} дней`);
                props.onChange?.(JSON.stringify([dateToSet, dateToSet]));
                return;
            }
        }

        props.onChange?.(JSON.stringify(value));
    };

    return (
        <DateRangeInput
            {...props}
            disabled={isDisabled}
            value={getValue()}
            onChange={handleChange}
            dateRangeKeys={{
                from: 'dateFrom',
                to: 'dateTo',
            }}
            dateRangeKey={paramName}
            disabledDate={disabledDate}
        />
    );
};
